import classnames from 'classnames';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import useToggle from 'dpl/shared/hooks/useToggle';
import { sendRudderstackEvent } from 'dpl/util/analytics';

import PaymentProtectionModal from './PaymentProtectionModal';

interface PaymentProtectionIconProps {
  className?: string;
  iconClassName?: string;
  size: string;
}

interface PaymentProtectionBannerProps {
  className?: string;
  variant?: 'breeder_page' | 'puppy_page';
  analytics?: {
    // defaults to the profile matching the variant
    view?: string;
  };
}

function PaymentProtectionIcon({
  className,
  iconClassName,
  size
}: PaymentProtectionIconProps) {
  return (
    <div className={classnames('PaymentProtectionIcon', className)}>
      <div
        className={classnames(
          'flex items-center justify-center bg-gray-200 br12 pt2 pb2 pt5-md pb5-md pl2 pr2 pl5-md pr5-md',
          iconClassName
        )}
      >
        <Icon
          className="primary"
          name="gooddog-shield"
          height={size}
          width={size}
        />
      </div>
    </div>
  );
}

export default function PaymentProtectionBanner({
  className,
  variant = 'breeder_page',
  analytics
}: PaymentProtectionBannerProps) {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);

  function openModal(e) {
    toggleIsModalOpen(true);

    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: e.target.textContent,
      view:
        analytics?.view ||
        (variant === 'puppy_page'
          ? ANALYTICS_VIEWS.PUPPY_PROFILE
          : ANALYTICS_VIEWS.BREEDER_PROFILE),
      view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_POLICY_LINK,
      view_feature_location:
        ANALYTICS_VIEW_FEATURE_LOCATIONS.PAYMENT_PROTECTION_BANNER
    });
  }

  function closeModal() {
    toggleIsModalOpen(false);
  }

  return (
    <div
      className={classnames(
        'PaymentProtectionBanner bg-white ba b--default br4 pt5 pb5 pt6-md pb6-md pl5 pr5 pl6-md pr6-md',
        { 'bn-l pv0-lg ph0-lg': variant === 'puppy_page' },
        className
      )}
    >
      <div
        className={classnames('flex-ns gap-32', {
          'items-center': variant === 'breeder_page'
        })}
      >
        <PaymentProtectionIcon
          className="hidden md:block"
          iconClassName={classnames({
            'pv4-md ph4-md': variant === 'puppy_page'
          })}
          size={variant === 'puppy_page' ? '32px' : '40px'}
        />
        <div>
          <div className="flex items-center gap-20">
            <PaymentProtectionIcon className="md:hidden" size="36px" />
            <h4 className="font-20 md:font-24 lh-title">
              You’re protected
              <br className="dn-ns" /> on Good Dog
            </h4>
          </div>
          <div className="font-16 md:font-18 mt4 mt2-md">
            <p className="secondary">
              <span>
                When you pay and communicate on Good Dog, you’re covered by
                Payment Protection.
              </span>{' '}
              <button
                type="button"
                className={classnames('fw-medium primary db mt4 mt1-md', {
                  'di-l': variant === 'puppy_page'
                })}
                onClick={openModal}
              >
                Learn more about how you’re protected
              </button>
            </p>
            <PaymentProtectionModal isOpen={isModalOpen} onClose={closeModal} />
          </div>
        </div>
      </div>
    </div>
  );
}
